import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import PublicLayout from '@/components/hydlib/PublicLayout';
import SuggestBookButton from '@/components/SuggestBookButton';
import DeliveryBalanceCard from '@/components/DeliveryBalanceCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = "'Space Mono', monospace";
const serifDisplay = "'Libre Caslon Display', serif";
const highlightBg = (a, s1, s2, s3) => `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(${a}) ${s1}%, var(--paper)) 5%, color-mix(in oklch, var(${a}) ${s2}%, var(--paper)) 52%, color-mix(in oklch, var(${a}) ${s3}%, var(--paper)) 94%, transparent 98.5%)`;

const isComingSoon = (b) => b.available_from && new Date(b.available_from) > new Date();
const isChildrens = (b) => (b.categories || []).some(c => /children/i.test(c));

function Catalog() {
    const { user, getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const isMember = !!user && (user.subscription_status === 'active' || user.role === 'staff');
    const showCart = !!user && user.subscription_status !== 'active' && user.role !== 'staff';

    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState(null);
    const [authors, setAuthors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
    const [cart, setCart] = useState(null);
    const [cartOpen, setCartOpen] = useState(false);
    const [addingToCart, setAddingToCart] = useState(null);
    const [tbrIds, setTbrIds] = useState(new Set());
    const debounceRef = useRef(null);

    // URL params are the source of truth
    const search = searchParams.get('search') || '';
    const author = searchParams.get('author') || '';
    const category = searchParams.get('category') || '';
    const page = parseInt(searchParams.get('page')) || 1;
    const sort = searchParams.get('sort') || 'title';
    const collection = searchParams.get('filter') || '';

    const updateParams = useCallback((patch) => {
        const next = { search, author, category, page: 1, sort, filter: collection, ...patch };
        const params = new URLSearchParams();
        if (next.search) params.set('search', next.search);
        if (next.author) params.set('author', next.author);
        if (next.category) params.set('category', next.category);
        if (next.page > 1) params.set('page', String(next.page));
        if (next.sort && next.sort !== 'title') params.set('sort', next.sort);
        if (next.filter) params.set('filter', next.filter);
        setSearchParams(params);
    }, [search, author, category, sort, collection, setSearchParams]);

    useEffect(() => { setSearchInput(search); }, [search]);

    const onSearchChange = (val) => {
        setSearchInput(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => updateParams({ search: val }), 450);
    };

    useEffect(() => {
        (async () => {
            try {
                const [aRes, cRes] = await Promise.all([
                    axios.get(`${API}/books/authors`, { headers: getAuthHeader() }),
                    axios.get(`${API}/books/categories`, { headers: getAuthHeader() }),
                ]);
                setAuthors(aRes.data);
                setCategories(cRes.data);
            } catch { /* ignore */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                if (collection === 'new-adults' || collection === 'new-kids') {
                    const ep = collection === 'new-kids' ? 'recently-added-children' : 'recently-added-adults';
                    const res = await axios.get(`${API}/books/${ep}?limit=60`, { headers: getAuthHeader() });
                    const list = Array.isArray(res.data) ? res.data : (res.data?.books || []);
                    if (!cancelled) { setBooks(list); setPagination(null); }
                } else {
                    const params = new URLSearchParams();
                    if (search) params.append('search', search);
                    if (author) params.append('author', author);
                    if (category) params.append('category', category);
                    params.append('page', String(page));
                    params.append('limit', '25');
                    if (sort && sort !== 'title') params.append('sort_by', sort);
                    const res = await axios.get(`${API}/books?${params.toString()}`, { headers: getAuthHeader() });
                    if (!cancelled) { setBooks(res.data.books); setPagination(res.data.pagination); }
                }
            } catch { /* ignore */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, author, category, page, sort, collection]);

    const fetchCart = useCallback(async () => {
        if (!showCart) return;
        try {
            const res = await axios.get(`${API}/rentals/cart`, { headers: getAuthHeader() });
            setCart(res.data);
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCart]);

    useEffect(() => { fetchCart(); }, [fetchCart]);

    useEffect(() => {
        if (!user || user.role === 'staff') return;
        axios.get(`${API}/tbr`, { headers: getAuthHeader() })
            .then(r => setTbrIds(new Set((r.data || []).map(t => t.book_id ?? t.id))))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    const cartItems = cart?.items || [];
    const cartBookIds = new Set(cartItems.map(it => it.book_id));
    const cartTotal = cartItems.reduce((s, it) => s + Number(it.price ?? it.rental_price ?? 0), 0);

    const handleRent = async (e, book) => {
        e.preventDefault();
        e.stopPropagation();
        if (!user) { navigate('/signup?mode=rent&redirect=/catalog'); return; }
        if (cartBookIds.has(book.id)) { setCartOpen(true); return; }
        setAddingToCart(book.id);
        try {
            await axios.post(`${API}/rentals/cart/items`, { book_id: book.id }, { headers: getAuthHeader() });
            window.dispatchEvent(new CustomEvent('rental-cart-updated'));
            await fetchCart();
            setCartOpen(true);
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to add to cart');
        } finally {
            setAddingToCart(null);
        }
    };

    const handleRemoveFromCart = async (bookId) => {
        try {
            const res = await axios.delete(`${API}/rentals/cart/items/${bookId}`, { headers: getAuthHeader() });
            setCart(res.data);
            window.dispatchEvent(new Event('rental-cart-updated'));
        } catch { /* ignore */ }
    };

    const toggleTbr = async (e, book) => {
        e.preventDefault();
        e.stopPropagation();
        if (!user) { navigate('/login'); return; }
        const inTbr = tbrIds.has(book.id);
        setTbrIds(prev => {
            const next = new Set(prev);
            if (inTbr) next.delete(book.id); else next.add(book.id);
            return next;
        });
        try {
            if (inTbr) await axios.delete(`${API}/tbr/${book.id}`, { headers: getAuthHeader() });
            else await axios.post(`${API}/tbr/${book.id}`, {}, { headers: getAuthHeader() });
        } catch {
            setTbrIds(prev => {
                const next = new Set(prev);
                if (inTbr) next.add(book.id); else next.delete(book.id);
                return next;
            });
        }
    };

    const nFilters = (author ? 1 : 0) + (category ? 1 : 0);
    const goToPage = (p) => {
        updateParams({ page: p });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const totalPages = pagination?.total_pages || 1;
    const currentPage = pagination?.current_page || 1;
    const pageWindow = Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(p => p >= currentPage - 2 && p <= currentPage + 2);

    return (
        <PublicLayout theme="archive" grid="--a3" gridStrength={28}>
            <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a1) 0 5px, transparent 6px)' }} />

            <div className="hydlib-pad" style={{ position: 'relative', maxWidth: 1240, margin: '0 auto', padding: '64px 48px', boxSizing: 'border-box' }}>
                <h1 style={{ position: 'relative', fontFamily: serifDisplay, fontWeight: 400, fontSize: 52, margin: '0 0 8px' }} data-testid="catalog-heading">
                    <span style={{ background: 'var(--bg)', boxShadow: '8px 0 0 var(--bg), -8px 0 0 var(--bg)' }}>The catalog</span>
                </h1>
                <p style={{ position: 'relative', fontFamily: mono, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 36px' }}>
                    <span style={{ background: 'var(--bg)', boxShadow: '6px 0 0 var(--bg), -6px 0 0 var(--bg)' }}>Browse, reserve, and it&apos;s in the box by tomorrow</span>
                </p>

                {user && user.role !== 'staff' && (
                    <div style={{ marginBottom: 24 }}>
                        <DeliveryBalanceCard user={user} getAuthHeader={getAuthHeader} variant="banner" />
                    </div>
                )}

                {/* Collection banner (?filter=new-adults / new-kids from the landing shelf) */}
                {(collection === 'new-adults' || collection === 'new-kids') && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }} data-testid="collection-banner">
                        <span style={{ background: 'var(--a2)', color: 'var(--o2)', border: '1px solid var(--line)', fontFamily: mono, fontWeight: 700, fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', padding: '8px 16px', transform: 'rotate(-1deg)', display: 'inline-block' }}>
                            {collection === 'new-kids' ? 'Recently added · for the little ones' : 'Recently added · for the grown-ups'}
                        </span>
                        <button onClick={() => updateParams({ filter: '' })} data-testid="clear-collection-btn" style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--a4)', fontFamily: mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', textDecoration: 'underline', padding: 0 }}>
                            ✕ show everything
                        </button>
                    </div>
                )}

                {/* Search + filter dropdowns + Cart controls */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 20 }}>
                    <input
                        type="text"
                        placeholder="Search title or author…"
                        value={searchInput}
                        onChange={(e) => onSearchChange(e.target.value)}
                        data-testid="catalog-search-input"
                        style={{ flex: 1, minWidth: 220, border: '1px solid var(--line)', background: 'var(--paper)', padding: '12px 16px', fontSize: 16, color: 'var(--ink)', fontFamily: 'inherit' }}
                    />
                    <select
                        value={category}
                        onChange={(e) => updateParams({ category: e.target.value })}
                        data-testid="catalog-genre-select"
                        style={{ cursor: 'pointer', maxWidth: 190, border: '1px solid var(--line)', background: category ? 'var(--ink)' : 'var(--paper)', color: category ? 'var(--paper)' : 'var(--ink)', fontFamily: mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', padding: '12px 10px' }}
                    >
                        <option value="">All genres</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select
                        value={author}
                        onChange={(e) => updateParams({ author: e.target.value })}
                        data-testid="catalog-author-select"
                        style={{ cursor: 'pointer', maxWidth: 190, border: '1px solid var(--line)', background: author ? 'var(--ink)' : 'var(--paper)', color: author ? 'var(--paper)' : 'var(--ink)', fontFamily: mono, fontSize: 12, letterSpacing: 1, padding: '12px 10px', textTransform: author ? 'none' : 'uppercase' }}
                    >
                        <option value="">All authors</option>
                        {authors.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <select
                        value={sort}
                        onChange={(e) => updateParams({ sort: e.target.value })}
                        data-testid="catalog-sort-select"
                        style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', fontFamily: mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', padding: '12px 10px' }}
                    >
                        <option value="title">A → Z</option>
                        <option value="recent">Most recent</option>
                        <option value="popular">Most popular</option>
                    </select>
                    {(nFilters > 0 || search) && (
                        <button onClick={() => { updateParams({ search: '', author: '', category: '' }); }} data-testid="clear-filters-btn" style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--a4)', fontFamily: mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', textDecoration: 'underline', padding: 0 }}>
                            ✕ Clear
                        </button>
                    )}
                    {showCart && (
                        <button
                            onClick={() => setCartOpen(o => !o)}
                            data-testid="catalog-cart-btn"
                            style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--a2)', color: 'var(--o2)', fontFamily: mono, fontWeight: 700, fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', padding: '12px 22px' }}
                        >
                            🧺 Cart ({cartItems.length})
                        </button>
                    )}
                </div>

                {/* Inline cart panel */}
                {showCart && cartOpen && (
                    <div style={{ border: '1px solid var(--line)', borderTop: '6px solid var(--a2)', background: 'var(--paper)', padding: '22px 24px', marginBottom: 28, boxShadow: '6px 6px 0 color-mix(in oklch, var(--line) 30%, transparent)' }} data-testid="catalog-cart-panel">
                        <div style={{ fontFamily: serifDisplay, fontSize: 24, marginBottom: 4 }}>Your rental cart</div>
                        <p style={{ fontFamily: "'Caveat', cursive", fontSize: 20, fontWeight: 600, color: 'var(--a1)', margin: '0 0 16px', transform: 'rotate(-1deg)', width: 'fit-content' }}>₹200 per adult book · ₹100 per children&apos;s book</p>
                        {cartItems.length > 0 ? (
                            <>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                                    {cartItems.map(it => (
                                        <div key={it.book_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderBottom: '1px dashed var(--line)', paddingBottom: 6, fontSize: 15 }} data-testid={`cart-panel-item-${it.book_id}`}>
                                            <span>{it.title}</span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                <span style={{ fontFamily: mono, fontSize: 13 }}>₹{it.price ?? it.rental_price}</span>
                                                <button onClick={() => handleRemoveFromCart(it.book_id)} data-testid={`cart-panel-remove-${it.book_id}`} style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--a4)', fontFamily: mono, fontSize: 12 }}>✕</button>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                                    <span style={{ fontFamily: serifDisplay, fontSize: 22 }}>Total: ₹{cartTotal}</span>
                                    <Link to="/rental-cart" className="hydlib-highlight" data-testid="cart-panel-checkout" style={{ fontFamily: mono, fontWeight: 700, fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', background: highlightBg('--a2', 62, 46, 68), padding: '12px 24px' }}>
                                        Go to cart → pay ₹{cartTotal}
                                    </Link>
                                </div>
                            </>
                        ) : (
                            <div style={{ fontSize: 15, fontStyle: 'italic', opacity: 0.75 }}>Nothing here yet — hit &quot;Rent&quot; on any book to add it.</div>
                        )}
                    </div>
                )}

                {/* Books grid */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '64px 0', fontStyle: 'italic', opacity: 0.7 }} data-testid="catalog-loading">Fetching the shelves…</div>
                ) : books.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '64px 0' }} data-testid="catalog-empty">
                        <p style={{ fontFamily: serifDisplay, fontSize: 24, margin: '0 0 10px' }}>Nothing on this shelf.</p>
                        <button onClick={() => { updateParams({ search: '', author: '', category: '', filter: '' }); }} style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--a4)', fontFamily: mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', textDecoration: 'underline', padding: 0 }}>
                            Clear filters and try again
                        </button>
                    </div>
                ) : (
                    <div className="hydlib-cat-grid">
                        {books.map(b => {
                            const coming = isComingSoon(b);
                            const reference = !!b.is_reference;
                            const available = (b.available_copies ?? 0) > 0;
                            const total = b.total_copies ?? 1;
                            const inCart = cartBookIds.has(b.id);
                            const inTbr = tbrIds.has(b.id);
                            const status = coming ? 'Coming soon'
                                : reference ? 'Reference only · read in library'
                                : available ? (total > 1 ? `${b.available_copies} of ${total} on the shelf` : 'On the shelf')
                                : 'On hold · join queue';
                            const statusColor = coming ? 'var(--a5)' : reference ? 'var(--a1)' : available ? 'var(--a3)' : 'var(--a4)';
                            const action = coming ? 'Coming soon →'
                                : reference ? 'In-library only →'
                                : isMember ? (available ? 'Reserve →' : 'Join queue →')
                                : showCart ? (!available ? 'Join queue →' : inCart ? 'In cart ✓' : (addingToCart === b.id ? 'Adding…' : `Rent — ₹${isChildrens(b) ? 100 : 200}`))
                                : `Rent — ₹${isChildrens(b) ? 100 : 200}`;
                            const rentable = !coming && !reference && available && (showCart || !user);
                            return (
                                <div key={b.id} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 8 }} data-testid={`catalog-book-${b.id}`}>
                                    <Link to={`/book/${b.id}`} className="hydlib-cover" style={{ position: 'relative', width: '100%', aspectRatio: '2 / 3', boxShadow: '5px 6px 0 color-mix(in oklch, var(--line) 25%, transparent)', background: 'var(--paper)', border: '1px solid var(--line)', display: 'block', overflow: 'hidden' }}>
                                        {b.cover_image_url ? (
                                            <img src={b.cover_image_url} alt={b.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 14, fontFamily: serifDisplay, fontSize: 16, lineHeight: 1.3, color: 'var(--ink)' }}>{b.title}</span>
                                        )}
                                    </Link>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                                        <Link to={`/book/${b.id}`} style={{ fontFamily: serifDisplay, fontSize: 17, lineHeight: 1.25, color: 'var(--ink)' }}>{b.title}</Link>
                                        <div style={{ fontSize: 13.5, opacity: 0.8 }}>{b.authors?.join(', ') || 'Unknown author'}</div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 'auto' }}>
                                            <span style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase', color: statusColor }} data-testid={`book-status-${b.id}`}>{status}</span>
                                            {user && user.role !== 'staff' && (
                                                <button onClick={(e) => toggleTbr(e, b)} title={inTbr ? 'Remove from TBR' : 'Add to TBR'} data-testid={`tbr-toggle-${b.id}`} style={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: 18, lineHeight: 1, color: inTbr ? 'var(--a1)' : 'var(--ink)', padding: 0 }}>
                                                    {inTbr ? '♥' : '♡'}
                                                </button>
                                            )}
                                            {rentable ? (
                                                <button onClick={(e) => handleRent(e, b)} disabled={addingToCart === b.id} className="hydlib-highlight" data-testid={`book-action-${b.id}`} style={{ cursor: 'pointer', border: 'none', fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ink)', background: highlightBg('--a2', 58, 42, 64), padding: '4px 10px', whiteSpace: 'nowrap' }}>
                                                    {action}
                                                </button>
                                            ) : (
                                                <Link to={`/book/${b.id}`} className="hydlib-highlight" data-testid={`book-action-${b.id}`} style={{ fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ink)', background: highlightBg('--a2', 58, 42, 64), padding: '4px 10px', whiteSpace: 'nowrap' }}>
                                                    {action}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                {!loading && pagination && totalPages > 1 && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 44, fontFamily: mono, fontSize: 13, flexWrap: 'wrap' }} data-testid="pagination">
                            <button onClick={() => pagination.has_prev && goToPage(currentPage - 1)} data-testid="prev-page-btn" style={{ cursor: pagination.has_prev ? 'pointer' : 'default', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 'inherit', padding: '8px 14px', opacity: pagination.has_prev ? 1 : 0.35 }}>‹ prev</button>
                            {pageWindow[0] > 1 && (
                                <>
                                    <button onClick={() => goToPage(1)} style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 'inherit', padding: '8px 13px' }}>1</button>
                                    {pageWindow[0] > 2 && <span style={{ opacity: 0.5 }}>…</span>}
                                </>
                            )}
                            {pageWindow.map(p => (
                                <button key={p} onClick={() => goToPage(p)} style={{ cursor: 'pointer', border: '1px solid var(--line)', background: p === currentPage ? 'var(--ink)' : 'var(--paper)', color: p === currentPage ? 'var(--paper)' : 'var(--ink)', fontFamily: 'inherit', fontSize: 'inherit', padding: '8px 13px' }}>{p}</button>
                            ))}
                            {pageWindow[pageWindow.length - 1] < totalPages && (
                                <>
                                    {pageWindow[pageWindow.length - 1] < totalPages - 1 && <span style={{ opacity: 0.5 }}>…</span>}
                                    <button onClick={() => goToPage(totalPages)} style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 'inherit', padding: '8px 13px' }}>{totalPages}</button>
                                </>
                            )}
                            <button onClick={() => pagination.has_next && goToPage(currentPage + 1)} data-testid="next-page-btn" style={{ cursor: pagination.has_next ? 'pointer' : 'default', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 'inherit', padding: '8px 14px', opacity: pagination.has_next ? 1 : 0.35 }}>next ›</button>
                        </div>
                        <div style={{ textAlign: 'center', fontFamily: mono, fontSize: 12, letterSpacing: 1, opacity: 0.7, marginTop: 10 }} data-testid="page-info">
                            Page {currentPage} of {totalPages} · {pagination.total_count} books
                        </div>
                    </>
                )}

                {/* Book request */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', margin: '40px 0 0' }}>
                    <p style={{ fontFamily: "'Caveat', cursive", fontSize: 22, fontWeight: 600, margin: 0, transform: 'rotate(-1deg)', color: 'var(--a1)' }}>
                        can&apos;t find it? ask us — we take requests!
                    </p>
                    <SuggestBookButton />
                </div>
            </div>
        </PublicLayout>
    );
}

export default Catalog;
